import { format } from 'date-fns';
import { dataQueryUrlV1, structureQueryUrl, structureQueryUrlV1 } from '@dbmx/sdmx';
import {
  has,
  indexBy,
  isEmpty,
  isNil,
  mapValues,
  overProp,
  path,
  pick,
  propOr,
} from '../../utils/fp';
import {
  sdmxPeriodBoundaries,
  defaultFrequency,
  sdmxPeriod as defaultSettingsPeriod,
} from '../settings';
import {
  applyFormat,
  getDatesFromSdmxPeriod,
  replaceUndefinedDates,
  parseInclusiveDates,
  getAscendentDates,
  getEndOfYear,
  getAjustedDate,
  getSdmxPeriod,
  getSubstructedDate,
} from './frequency';
import { getDateInTheRange } from '../../utils/date';
import { DISPLAY_BOTH, DISPLAY_CODE, DISPLAY_LABEL } from '../../utils/constants';
import { LASTNOBSERVATIONS, LASTNPERIODS } from '../../utils/used-filter';
import { getDataV1HeaderFormat, getStructureHeaderFormat } from '../headers';
import { getIsV3, getRangeHeaderValue } from '../utils';
import { getTimePeriodBoundaries } from './accessors/constraints';
import { handleDataVersion } from './version';

const LAST_N_PERIODS_PARAM_KEY = 'lastNPeriods';

export const getFilename = ({ identifiers, dataquery, isFull }) => {
  const ids = [identifiers.agencyId, identifiers.code, identifiers.version];
  const timestamp = format(new Date(), 'yyyy-MM-dd HH-mm-ss');
  return [...ids, isNil(dataquery) || isFull ? 'complete' : 'filtered', timestamp].join(',');
};

export const getDefaultRouterParams = ({
  params,
  dataquery,
  timePeriodBoundaries,
  inclusiveBoundaries,
  frequencyArtefact,
  hasDataAvailability,
  yearsFromSearch = [],
}) => {
  const startPeriod = yearsFromSearch[0] ?? null;
  const endPeriod = yearsFromSearch.length > 1 ? yearsFromSearch.at(-1) : startPeriod;
  const annotLastNPeriods = params?.[LAST_N_PERIODS_PARAM_KEY];
  const dq = isNil(dataquery) ? '' : dataquery;
  const rawFrequency = dq.split('.')[frequencyArtefact?.index];
  const frequency = isEmpty(rawFrequency) || isNil(rawFrequency) ? defaultFrequency : rawFrequency;
  const facetedPeriod = [startPeriod, endPeriod];
  const periodBoundaries = hasDataAvailability
    ? getAscendentDates(
        parseInclusiveDates(
          inclusiveBoundaries,
          frequency,
        )(replaceUndefinedDates(sdmxPeriodBoundaries)(timePeriodBoundaries)),
      )
    : sdmxPeriodBoundaries;

  const fromParams =
    has('startPeriod', params) || has('endPeriod', params)
      ? getDatesFromSdmxPeriod(frequency)([params.startPeriod, params.endPeriod])
      : defaultSettingsPeriod;
  const bounded = [fromParams[0], getAjustedDate(frequency)(getEndOfYear(fromParams.at(-1)))];
  const formatted = bounded.map(date =>
    applyFormat(frequency)(getDateInTheRange(periodBoundaries)(date)),
  );
  const period = isNil(formatted[0]) && isNil(formatted[1]) ? null : formatted;

  return {
    ...pick(['lastNObservations'], params),
    dataquery,
    period: !isNil(endPeriod) ? facetedPeriod : !isNil(annotLastNPeriods) ? null : period,
    frequency,
  };
};

export const getSelectedIdsIndexed = dimensions =>
  mapValues(
    dimension =>
      propOr([], 'values', dimension).reduce(
        (acc, value) => (value.isSelected ? [...acc, value.id] : acc),
        [],
      ),
    indexBy(dimension => dimension.id, dimensions),
  );

export const setSelectedDimensionsValues = (dataquery, dimensions) => {
  const splitDataquery = dataquery.split('.');
  return dimensions.map((dimension, index) => {
    if (isEmpty(dimension)) return {};
    const partialDataquery = splitDataquery[index];
    if (isNil(partialDataquery)) return {};
    const selectedIds = new Set(partialDataquery.split('+'));
    return overProp(
      'values',
      values =>
        values.map(value => (selectedIds.has(value.id) ? { ...value, isSelected: true } : value)),
      dimension,
    );
  });
};

export const getOnlyHasDataDimensions = dimensions =>
  dimensions.map(dimension => {
    // NB: R.either only uses its first two predicates, so the isAttribute check
    // was always ignored — behavior preserved
    if (isNil(dimension) || isEmpty(dimension)) return dimension;
    if (dimension.hasData === false) return {};
    return overProp('values', values => values.filter(value => value.hasData !== false), dimension);
  });

export const rawDataRequestArgsWrapper = (args, dataquery, params, externalReference, range) => {
  const base = !isNil(externalReference) ? { ...args, ...externalReference } : args;
  return {
    ...base,
    dataquery,
    params,
    ...(path(['datasource', 'hasRangeHeader'], args) ? { range } : {}),
  };
};

// The period and last-N params of a data request, from the ones in the url. A
// last-N-periods start is counted back from the end of the available periods.
export const getDataRequestParams = ({
  period: _period,
  lastNValue,
  lastNMode,
  frequency,
  availableTimePeriod,
}) => {
  const boundaries = getTimePeriodBoundaries(availableTimePeriod);
  const lastNObservations = lastNMode === LASTNOBSERVATIONS ? lastNValue : null;

  if (!isNil(_period)) {
    return {
      lastNObservations,
      startPeriod: _period[0],
      endPeriod: _period.at(-1),
    };
  }

  const period = [undefined, undefined];
  const formattedAvailablePeriod =
    isNil(boundaries) || isEmpty(boundaries)
      ? [undefined, undefined]
      : boundaries.map(p => (isNil(p) ? p : new Date(p.toString())));

  const diff =
    !isNil(formattedAvailablePeriod.at(-1)) && !isNil(lastNValue)
      ? getSubstructedDate(formattedAvailablePeriod.at(-1), lastNValue, frequency)
      : null;

  const newStartPeriod =
    !isNil(diff) && isNil(period[0]) ? getSdmxPeriod(frequency)(diff) : period[0];

  return {
    startPeriod: lastNMode === LASTNPERIODS ? newStartPeriod : period[0],
    endPeriod: period.at(-1),
    lastNObservations,
  };
};

// The url of a data request, from its raw args (rawDataRequestArgsWrapper).
// `agnostic` leaves the dimension at observation to the source.
export const getDataQueryUrl = ({ datasource, dataquery, identifiers, params }, { agnostic }) => {
  if (!datasource || !identifiers?.code) {
    return null;
  }
  return dataQueryUrlV1({
    ...identifiers,
    ...params,
    version: handleDataVersion(datasource)(identifiers.version),
    id: identifiers.code,
    endpoint: datasource.url,
    key: dataquery,
    dimensionAtObservation: agnostic ? null : undefined,
  });
};

// The url of the structure request of a dataflow: the dataflow and everything it
// references, its content constraints among them.
export const getStructureQueryUrl = ({ datasource, identifiers = {} }) => {
  if (!datasource?.url) {
    return null;
  }
  const isV3 = getIsV3(datasource);
  const { agencyId, code, version } = identifiers;
  const urlGenerator = isV3 ? structureQueryUrl : structureQueryUrlV1;
  return urlGenerator({
    agencyID: agencyId,
    id: code,
    version: isNil(version) || isEmpty(version) ? '~' : version,
    endpoint: isV3 ? datasource.urlv3 : datasource.url,
    resourceType: 'dataflow',
    detail: 'referencepartial',
    references: 'all',
    format: 'json',
  });
};

// The request of a dataflow structure at its url: in the format of the datasource
// and the locale of the page.
export const getStructureRequestArgs = (url, { datasource, locale }) => ({
  url,
  headers: {
    Accept: getStructureHeaderFormat(datasource),
    'Accept-Language': locale,
  },
});

// What api/sdmx is handed for a data request: the space id tells it whether the
// space takes a custom range header and a POST for a long url.
export const dataRequestArgsWrapper = requestArgs => {
  const { datasource, range, locale } = requestArgs;
  const headers = {
    Accept: getDataV1HeaderFormat(datasource),
    'Accept-Language': locale,
    'x-range': datasource?.hasRangeHeader && !isNil(range) ? getRangeHeaderValue(range) : null,
  };
  return {
    url: getDataQueryUrl(requestArgs, { agnostic: false }),
    headers,
    datasourceId: datasource?.id,
  };
};

const displayToLabelsAcceptHeader = display => {
  if (display === DISPLAY_LABEL) return 'labels=name';
  if (display === DISPLAY_BOTH) return 'labels=both';
  if (display === DISPLAY_CODE) return 'labels=code';
  return;
};

export const dataFileRequestArgsWrapper = isFull => (requestArgs, display) => {
  const { datasource, dataquery, identifiers, params, locale } = requestArgs;
  const _params = isFull ? {} : params;
  if (!datasource || !identifiers?.code) {
    return {};
  }
  const url = dataQueryUrlV1({
    ...identifiers,
    ..._params,
    version: handleDataVersion(datasource)(identifiers.version),
    id: identifiers.code,
    endpoint: datasource.url,
    key: isFull ? 'all' : dataquery,
  });

  const headers = {
    Accept: [getDataV1HeaderFormat(datasource, 'csv'), displayToLabelsAcceptHeader(display)].join(
      ';',
    ),
    'Accept-Language': locale,
  };

  return {
    url: `${url}&format=${display === DISPLAY_CODE ? 'csvfile' : 'csvfilewithlabels'}&lang=${locale}`,
    headers,
    filename: getFilename({ ...requestArgs, isFull }),
    datasourceId: requestArgs.datasource?.id,
    responseType: 'csv',
  };
};
